import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, ShoppingCartIcon, Upload, BarChart3, Menu } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Ticket, HandHelping, Briefcase,
  CircleUserRound, LayoutTemplate, Mail, LogOut, X,
  Layers, Tag, Tags, Warehouse, Inbox, MessageSquare, Users, Zap,
  Hammer, UserCog, Wallet, Receipt, TrendingUp, LayoutDashboard,
  Settings, CreditCard, BookOpen, Package, Sun,
} from 'lucide-react';
import axios from 'axios';
import dialog from '../utils/dialog';
import AuthContext from '../context/AuthContext';
import { usePluginContext } from '../context/PluginContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { capWord, hasCatalogSchema, configVersion, subscribeBusinessConfig, isSiteOnly } from '../utils/businessConfig';
import { useT } from '../i18n';

// Same short-lived cache the desktop Sidebar uses, so the two navs agree on
// subscription state without an extra request per page.
const SUB_CACHE_KEY = 'inikru_sub_status_cache';
const SUB_CACHE_TTL_MS = 60000;
const readSubCache = () => {
  try {
    const raw = sessionStorage.getItem(SUB_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > SUB_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};
const writeSubCache = (data) => {
  try {
    sessionStorage.setItem(SUB_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* private mode */ }
};

/**
 * Mobile bottom navigation. Mirrors the SAME nav model as the desktop
 * Sidebar: identical destinations, plugin gating (hidden unless installed),
 * and the subscription gate on Upload. Previously it listed legacy routes
 * (Designer KYC / Designer Profile) the desktop nav had dropped, bypassed
 * the Upload gate, and couldn't reach any plugin page or the Plugin Store.
 */
const MobileBottomNav = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext) || {};
  const { isPluginEnabled } = usePluginContext();
  const t = useT();
  const [showMore, setShowMore] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => readSubCache() || { is_active: false });

  useEffect(() => {
    if (!authToken?.access) return;
    if (readSubCache()) return; // fresh enough
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/get_subscription_status/`, {
        headers: { Authorization: `Bearer ${authToken.access}` },
      })
      .then((res) => {
        if (cancelled) return;
        setSubscriptionStatus(res.data);
        writeSubCache(res.data);
      })
      .catch((err) => devLog.error('MobileBottomNav subscription fetch failed:', err));
    return () => { cancelled = true; };
  }, [authToken?.access]);

  const handleLockedUpload = () => {
    const trialUsed = !!subscriptionStatus.has_used_free_trial;
    dialog.fire({
      title: trialUsed ? t('sub.trial_ended_title', 'Your free trial has ended') : t('sub.start_trial_title', 'Activate Your Free Trial'),
      text: trialUsed
        ? t('sub.trial_ended_text', 'Choose a plan to keep uploading products and selling on your store.')
        : t('sub.start_trial_text', 'Start your free 15-day trial to upload products and build your store.'),
      icon: 'info',
      confirmButtonText: trialUsed ? t('sub.view_plans', 'View Plans') : t('sub.start_trial', 'Start Free Trial'),
      confirmButtonColor: '#B40A0A',
      showCancelButton: true,
      cancelButtonText: t('action.later', 'Later'),
    }).then((result) => {
      if (result.isConfirmed) navigate('/store-settings#billing');
    });
  };

  const uploadLocked = !subscriptionStatus.is_active;

  // The business config decides which "Catalog" this nav shows, and it is a
  // module singleton that often lands AFTER the first render (see the same note
  // in Sidebar.js). Compare on mount as well as subscribing, or a business that
  // has just logged in sits on the wrong entry until it navigates again.
  const [cfgVersion, setCfgVersion] = useState(configVersion);
  useEffect(() => {
    if (configVersion() !== cfgVersion) setCfgVersion(configVersion());
    return subscribeBusinessConfig(setCfgVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const schemaCatalog = hasCatalogSchema();
  const siteOnly = isSiteOnly();

  const mainTabs = siteOnly ? [
    // Site mode (portfolio): the site, the work, the numbers — no store tabs.
    { icon: HomeIcon, label: t('nav.website', 'Website'), to: '/design' },
    { icon: Briefcase, label: t('nav.my_work', 'My Work'), to: '/portfolio' },
    { icon: BarChart3, label: t('nav.analytics', 'Analytics'), to: '/analytics' },
  ] : [
    // Same rule as the sidebar: "Products" opens the products. Fashion's list
    // is /homepage; every schema business has a real catalogue page.
    { icon: HomeIcon, label: t('nav.products', 'Products'), to: schemaCatalog ? '/catalog' : '/homepage' },
    { icon: ShoppingCartIcon, label: t('nav.orders', 'Orders'), to: '/Orders' },
    { icon: Upload, label: t('nav.add', 'Add'), to: '/UploadProduct', locked: uploadLocked },
    { icon: BarChart3, label: t('nav.analytics', 'Analytics'), to: '/analytics' },
  ];

  // Grouped exactly like the desktop sidebar; plugin items hidden unless installed.
  const moreSections = (siteOnly ? [
    {
      title: t('nav.group.site', 'My Website'),
      items: [
        { icon: Sun, label: t('nav.today', 'Today'), to: '/today' },
        { icon: Globe, label: t('nav.seo', 'SEO'), to: '/seo-settings' },
      ],
    },
    {
      title: t('nav.group.account', 'Account'),
      items: [
        { icon: CircleUserRound, label: t('nav.profile', 'Profile'), to: '/Profile' },
        { icon: CreditCard, label: t('nav.plan', 'Plan'), to: '/store-settings#billing' },
        { icon: Mail, label: t('nav.support', 'Support'), to: '/Contact' },
      ],
    },
  ] : [
    {
      title: t('nav.group.products', 'Products'),
      items: [
        // The product LIST for schema-driven businesses. Same gate as the
        // desktop Sidebar and App.js's CatalogEntry — `hasCatalogSchema()`.
        schemaCatalog && { icon: Package, label: t('nav.catalog', capWord('catalog', 'Catalog')), to: '/catalog' },
        isPluginEnabled('inventory') && { icon: Warehouse, label: t('nav.inventory', 'Inventory'), to: '/inventory' },
        { icon: Layers, label: t('nav.collections', 'Collections'), to: '/collections-manager' },
        { icon: Tag, label: t('nav.categories', 'Categories'), to: '/categories' },
        // Fashion's "Catalog" is the fabric-care / size-defaults screen. A
        // schema business has no use for it AND already has a Catalog entry
        // above, so showing both would be two things with the same name.
        !schemaCatalog && { icon: Tags, label: t('nav.catalog', 'Catalog'), to: '/catalog-settings' },
        { icon: Ticket, label: t('nav.coupons', 'Coupons'), to: '/my-coupons' },
      ],
    },
    {
      title: t('nav.group.orders', 'Orders'),
      items: [
        { icon: BookOpen, label: t('nav.order_book', 'Order Book'), to: '/orderbook' },
        isPluginEnabled('unified_inbox') && { icon: Inbox, label: t('nav.unified_inbox', 'Unified Inbox'), to: '/orders-unified' },
        isPluginEnabled('whatsapp') && { icon: MessageSquare, label: t('nav.whatsapp', 'WhatsApp'), to: '/whatsapp' },
        isPluginEnabled('bid_requests') && { icon: HandHelping, label: t('nav.custom_requests', 'Custom Requests'), to: '/Proposals' },
        isPluginEnabled('customers') && { icon: Users, label: t('nav.customers', 'Customers'), to: '/customers' },
        isPluginEnabled('automations') && { icon: Zap, label: t('nav.automations', 'Automations'), to: '/automations' },
      ],
    },
    {
      title: t('nav.group.business', 'Business'),
      items: [
        // The post-login landing (UI_CONSOLIDATION 4A). On mobile the sidebar
        // toggle is hidden, so this drawer is the ONLY way back to Today once
        // the shopkeeper has tapped one of the four bottom tabs. Ungated for
        // the same reason it is ungated in the sidebar: the server decides
        // which cards a business can even receive.
        { icon: Sun, label: t('nav.today', 'Today'), to: '/today' },
        isPluginEnabled('daily_ops') && { icon: LayoutDashboard, label: t('nav.day_summary', 'Day Summary'), to: '/dashboard' },
        isPluginEnabled('email_marketing') && { icon: Mail, label: t('nav.email_marketing', 'Email Marketing'), to: '/email-marketing' },
        isPluginEnabled('production_board') && { icon: Hammer, label: t('nav.production', 'Production'), to: '/production' },
        isPluginEnabled('team') && { icon: UserCog, label: t('nav.team', 'Team'), to: '/karigars' },
        isPluginEnabled('payments') && { icon: Wallet, label: t('nav.payments', 'Payments'), to: '/payments' },
        isPluginEnabled('expenses') && { icon: Receipt, label: t('nav.expenses', 'Expenses'), to: '/expenses' },
        isPluginEnabled('suppliers') && { icon: Briefcase, label: t('nav.suppliers', 'Suppliers'), to: '/suppliers' },
        isPluginEnabled('report_pnl') && { icon: TrendingUp, label: t('nav.pnl_report', 'P&L Report'), to: '/reports/pnl' },
        isPluginEnabled('report_gstr1') && { icon: Receipt, label: t('nav.gstr1', 'GSTR-1'), to: '/reports/gstr1' },
        isPluginEnabled('report_goals') && { icon: TrendingUp, label: t('nav.goals', 'Goals'), to: '/reports/goals' },
      ],
    },
    {
      title: t('nav.group.store_short', 'Store'),
      items: [
        { icon: LayoutTemplate, label: t('nav.themes', 'Themes'), to: '/TemplateThemes' },
        { icon: Globe, label: t('nav.seo', 'SEO'), to: '/seo-settings' },
        // Plugin Store intentionally NOT in the nav (owner decision, 2026-07-11).
      ],
    },
    {
      title: t('nav.group.account', 'Account'),
      items: [
        { icon: CircleUserRound, label: t('nav.brand_profile', 'Brand Profile'), to: '/Profile' },
        { icon: CreditCard, label: t('nav.connect_payments', 'Connect Payments'), to: '/connect-payments' },
        { icon: BarChart3, label: t('nav.earnings', 'Earnings'), to: '/earnings' },
        { icon: Settings, label: t('nav.store_settings', 'Store Settings'), to: '/store-settings' },
        { icon: Mail, label: t('nav.support', 'Support'), to: '/Contact' },
      ],
    },
  ])
    .map((section) => ({ ...section, items: section.items.filter(Boolean) }))
    .filter((section) => section.items.length > 0);

  const isActive = (to) => location.pathname === to;
  const isMoreActive = moreSections.some((s) => s.items.some((item) => location.pathname === item.to));

  return (
    <>
      {/* More Menu Bottom Sheet */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-[9998]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMore(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-[20px] shadow-2xl"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-line rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                <h3 className="font-display text-lg font-extrabold tracking-tight text-ink">{t('nav.more', 'More')}</h3>
                <button
                  type="button"
                  onClick={() => setShowMore(false)}
                  aria-label={t('action.close', 'Close')}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Grouped items */}
              <div className="max-h-[60vh] overflow-y-auto px-4 pb-2">
                {moreSections.map((section) => (
                  <div key={section.title} className="pt-3">
                    <p className="px-1 font-display text-[10.5px] font-bold uppercase tracking-[0.14em] text-gray-500">
                      {section.title}
                    </p>
                    <div className="grid grid-cols-3 gap-1 pt-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.to);
                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setShowMore(false)}
                            className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all active:scale-95 ${
                              active
                                ? 'bg-orange-50 text-primary'
                                : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                            }`}
                          >
                            <div className={`w-11 h-11 rounded-[10px] flex items-center justify-center ${
                              active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
                            }`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className={`text-[11px] font-medium text-center leading-tight ${
                              active ? 'text-primary' : 'text-gray-600'
                            }`}>
                              {item.label}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Logout */}
              <div className="px-4 pb-6 pt-2 border-t border-line">
                <button
                  onClick={() => {
                    setShowMore(false);
                    if (onLogout) onLogout();
                  }}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-[10px] text-red-600 bg-red-50 active:bg-red-100 font-display font-bold text-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#DC2626]"
                >
                  <LogOut className="w-4.5 h-4.5" />
                  {t('nav.logout', 'Log out')}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9997] bg-white border-t border-line lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                onClick={(e) => {
                  // Same gate as the desktop sidebar: locked Upload explains
                  // itself instead of dumping the user on a blocked page.
                  if (tab.locked) {
                    e.preventDefault();
                    handleLockedUpload();
                  }
                }}
                className={`flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform ${
                  tab.locked ? 'opacity-50' : ''
                }`}
              >
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-primary rounded-b-full"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <div className={`w-10 h-10 flex items-center justify-center rounded-[10px] transition-all ${
                  active ? 'bg-orange-50' : ''
                }`}>
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors ${
                      active ? 'text-primary' : 'text-gray-400'
                    }`}
                    strokeWidth={active ? 2.2 : 1.8}
                  />
                </div>
                <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
                  active ? 'text-primary' : 'text-gray-400'
                }`}>
                  {tab.label}
                </span>
              </Link>
            );
          })}

          {/* More button */}
          <button
            type="button"
            onClick={() => setShowMore(true)}
            aria-haspopup="dialog"
            aria-expanded={showMore}
            className="flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#B40A0A]"
          >
            {isMoreActive && (
              <motion.div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-primary rounded-b-full"
              />
            )}
            <div className={`w-10 h-10 flex items-center justify-center rounded-[10px] transition-all ${
              isMoreActive ? 'bg-orange-50' : ''
            }`}>
              <Menu
                className={`w-[22px] h-[22px] transition-colors ${
                  isMoreActive ? 'text-primary' : 'text-gray-400'
                }`}
                strokeWidth={isMoreActive ? 2.2 : 1.8}
              />
            </div>
            <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
              isMoreActive ? 'text-primary' : 'text-gray-400'
            }`}>
              {t('nav.more', 'More')}
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
